import { useState, useEffect } from 'react';
import apiClient from 'utils/apiClient';

const useReview = (arg) => {
  // State
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);

  // Determine mode and IDs
  const isTaskMode = typeof arg === 'string';
  const taskId = isTaskMode ? arg : arg?.taskId;
  const helperUserId = !isTaskMode ? arg?.helperUserId : undefined;
  const requesterUserId = !isTaskMode ? arg?.requesterUserId : undefined;

  // Initialize page and size if provided
  useEffect(() => {
    if (!isTaskMode && arg) {
      if (typeof arg.page === 'number') setPage(arg.page);
      if (typeof arg.size === 'number') setSize(arg.size);
    }
  }, [isTaskMode, arg]);

  const fetchReviews = async (override = {}) => {
    const currentPage = override.page ?? page;
    const currentSize = override.size ?? size;

    let url = '';
    let paginated = false;

    if (taskId) {
      url = `/olitask-service/api/v1/task-reviews/task/${taskId}`;
      paginated = false;
    } else if (helperUserId) {
      url = `/olitask-service/api/v1/task-reviews/helpers/${helperUserId}/reviews?page=${currentPage}&size=${currentSize}`;
     
      paginated = true;
    } else if (requesterUserId) {
      url = `/olitask-service/api/v1/task-reviews/requesters/${requesterUserId}/reviews?page=${currentPage}&size=${currentSize}`;
      paginated = true;
    } else {
      return; // nothing to fetch
    }

    setLoading(true);
    setError(null);
    try {
      const token = sessionStorage.getItem('authToken');
      const response = await apiClient.get(url, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined
      });

      if (paginated) {
        const data = response.data || {};
        setReviews(Array.isArray(data.content) ? data.content : []);
        setTotalPages(typeof data.totalPages === 'number' ? data.totalPages : 0);
        setTotalElements(typeof data.totalElements === 'number' ? data.totalElements : 0);
      } else {
        setReviews(Array.isArray(response.data) ? response.data : []);
        setTotalPages(1);
        setTotalElements(Array.isArray(response.data) ? response.data.length : 0);
      }
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  const updateReviewStatus = async (reviewId, status) => {
    await apiClient.put(
      `/olitask-service/api/v1/task-reviews/${reviewId}/status`,
      null,
      { params: { status } }
    );
    // Refresh after update
    if (helperUserId || requesterUserId) {
      await fetchReviews({ page, size });
    } else if (taskId) {
      await fetchReviews();
    }
  };

  const deleteReview = async (reviewId, type = 'SOFT') => {
    await apiClient.delete(`/olitask-service/api/v1/task-reviews/${reviewId}`, {
      params: { type }
    });
    // Refresh after delete
    if (helperUserId || requesterUserId) {
      await fetchReviews({ page, size });
    } else if (taskId) {
      await fetchReviews();
    }
  };

  // Fetch on param changes
  useEffect(() => {
    if (taskId) {
      fetchReviews();
    }
  }, [taskId]);

  useEffect(() => {
    if (helperUserId || requesterUserId) {
      fetchReviews({ page, size });
    }
  }, [helperUserId, requesterUserId, page, size]);

  return {
    reviews,
    loading,
    error,
    fetchReviews,
    refetch: fetchReviews,
    updateReviewStatus,
    deleteReview,
    page,
    setPage,
    size,
    setSize,
    totalPages,
    totalElements,
  };
};

export default useReview;
